import { useState, type FormEvent } from 'react';
import { KeyRound, LogOut } from 'lucide-react';
import { useSession } from '@/app/session-context';
import { api, errorMessage } from '@/lib/api/client';
import { Button, ErrorState, Field, Input } from '@/ui/primitives';

const MIN_LENGTH = 12;

/**
 * Shown INSTEAD of the application when the account still owes a password
 * change — not as a banner, not as a nag, and not as a route someone can
 * navigate away from.
 *
 * The server refuses everything else with PASSWORD_CHANGE_REQUIRED regardless,
 * so this screen is not the control; it is the explanation. A user who saw
 * only 403s everywhere would file a bug about a broken account.
 *
 * The way out other than changing it is logging out, and that stays available
 * on purpose: someone handed a temporary password on the wrong account must
 * not be trapped in it.
 */
export function ChangePasswordRequired() {
  const { user, logout } = useSession();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Checked as the user types, so the button is not a guessing game. The
  // server checks all of it again — this is courtesy, not enforcement.
  const tooShort = next.length > 0 && next.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsCurrent = next.length > 0 && next === current;
  const ready =
    current.length > 0 && next.length >= MIN_LENGTH && next === confirm && !sameAsCurrent;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
      // Every session was revoked server-side, including this one — so the
      // only honest thing to do is send the user back to the login screen with
      // the password they just chose.
      await logout();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    // `min-h-dvh` rather than `min-h-screen`: `100vh` on a phone is the screen
    // *behind* the browser chrome, so the last field sat under the address bar.
    // Vertical centring is a desktop-only luxury here — with a keyboard open it
    // pushes the card off the top of the screen, so on a phone the card starts
    // at the top and the page scrolls instead.
    <div className="bg-surface-muted pt-safe pb-safe px-safe flex min-h-dvh justify-center md:items-center">
      <div className="w-full p-4">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-6 text-center">
            <div className="bg-primary/10 text-primary mx-auto mb-3 flex size-12 items-center justify-center rounded-xl">
              <KeyRound size={22} />
            </div>
            <h1 className="text-fg text-xl font-semibold">Parolanızı değiştirin</h1>
            {/* The address is not interpolated bare: with no user the sentence
                would start " hesabına…", which reads like a rendering bug. */}
            <p className="text-fg-muted mt-1 text-sm">
              {user?.email ? `${user.email} hesabına` : 'Hesabınıza'} geçici bir parola ile giriş
              yaptınız. Devam etmek için kendi parolanızı belirleyin.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="border-border bg-surface space-y-4 rounded-2xl border p-4 shadow-sm md:p-6"
          >
            <Field label="Mevcut parola" htmlFor="current-password">
              <Input
                id="current-password"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoFocus
              />
            </Field>

            <Field
              label="Yeni parola"
              htmlFor="new-password"
              hint={`En az ${MIN_LENGTH} karakter. Uzunluk, karmaşıklıktan daha çok işe yarar — hatırlayacağınız bir cümle seçin.`}
              error={
                tooShort
                  ? `En az ${MIN_LENGTH} karakter olmalı.`
                  : sameAsCurrent
                    ? 'Yeni parola mevcut parolayla aynı olamaz.'
                    : undefined
              }
            >
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                value={next}
                onChange={(e) => setNext(e.target.value)}
              />
            </Field>

            <Field
              label="Yeni parola (tekrar)"
              htmlFor="confirm-password"
              error={mismatch ? 'İki parola aynı değil.' : undefined}
            >
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </Field>

            {error && <ErrorState message={error} />}

            {/* Said before it happens, not discovered afterwards. */}
            <p className="text-fg-muted text-xs">
              Parolanız değiştiğinde açık olan tüm oturumlar kapanır ve yeniden giriş yapmanız
              istenir.
            </p>

            <Button
              type="submit"
              variant="primary"
              className="w-full"
              busy={busy}
              disabled={!ready}
            >
              Parolayı değiştir
            </Button>
          </form>

          {/* The only way out of this screen, so it gets a real hit area on a
              phone — `md:min-h-0` leaves the desktop row exactly as it was. */}
          <button
            type="button"
            onClick={() => void logout()}
            className="text-fg-muted hover:text-fg mx-auto mt-4 flex min-h-11 touch-manipulation items-center gap-1.5 px-3 text-sm md:min-h-0 md:px-0"
          >
            <LogOut size={14} /> Çıkış yap
          </button>
        </div>
      </div>
    </div>
  );
}
